"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { ArrowLeft, Download, Mail, Monitor, Package, Star } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { track } from "@/components/analytics/track";
import { effectiveEmailSchema } from "@/lib/validation/email";

type ProductOs = "windows" | "macos" | "linux";
type CheckoutPlan = "starter" | "professional";
type CheckoutInterval = "monthly" | "yearly";

type ProductAsset = {
  id: string;
  os: ProductOs;
  arch: string;
  format: string;
  displayName: string;
  recommended?: boolean;
};

type OsOption = {
  id: ProductOs;
  label: string;
};

type Product = {
  id: string;
  name: string;
  version?: string;
  hasOsVariants?: boolean;
  osOptions?: OsOption[];
  assets?: ProductAsset[];
};

function parseCheckoutPlan(value: string | null): CheckoutPlan | null {
  return value === "starter" || value === "professional" ? value : null;
}

function parseCheckoutInterval(value: string | null): CheckoutInterval | null {
  return value === "monthly" || value === "yearly" ? value : null;
}

function subscribeHref(options: {
  email?: string;
  plan: CheckoutPlan | null;
  interval: CheckoutInterval | null;
}): string {
  const params = new URLSearchParams();
  const email = options.email?.trim();
  if (email) params.set("email", email);
  if (options.plan) params.set("plan", options.plan);
  if (options.interval) params.set("interval", options.interval);
  const qs = params.toString();
  return qs ? `/subscribe?${qs}` : "/subscribe";
}

export default function ProductDownload({
  basicMonthlyLabel,
}: {
  basicMonthlyLabel: string;
}) {
  const t = useTranslations("Product");
  const tc = useTranslations("Common");
  const locale = useLocale();
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedOs, setSelectedOs] = useState<ProductOs | null>(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [productName, setProductName] = useState<string | null>(null);
  const [expiryHours, setExpiryHours] = useState<number>(2);
  const [checkoutPlan, setCheckoutPlan] = useState<CheckoutPlan | null>(null);
  const [checkoutInterval, setCheckoutInterval] =
    useState<CheckoutInterval | null>(null);

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    setCheckoutPlan(parseCheckoutPlan(search.get("plan")));
    setCheckoutInterval(parseCheckoutInterval(search.get("interval")));
    const preferredId = search.get("product")?.trim() || null;

    fetch("/api/product/list")
      .then((res) => res.json())
      .then((data: { products?: Product[] }) => {
        const list = data.products ?? [];
        setProducts(list);
        if (preferredId && list.some((p) => p.id === preferredId)) {
          setSelectedProductId(preferredId);
        } else if (list.length === 1) {
          setSelectedProductId(list[0]!.id);
        }
      })
      .catch(() => setProducts([]))
      .finally(() => setProductsLoading(false));
  }, []);

  const selectedProduct = products.find((p) => p.id === selectedProductId);
  const osAssets =
    selectedProduct?.assets?.filter((a) => a.os === selectedOs) ?? [];

  const handleEmailBlur = () => {
    if (!email.trim()) {
      setEmailError("");
      return;
    }
    const result = effectiveEmailSchema.safeParse(email);
    setEmailError(
      result.success ? "" : result.error.issues[0]?.message ?? "Invalid email"
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    setEmailSent(false);

    const emailResult = effectiveEmailSchema.safeParse(email);
    if (!emailResult.success) {
      setEmailError(
        emailResult.error.issues[0]?.message ?? "Invalid email"
      );
      return;
    }
    setEmailError("");

    if (!selectedProductId) {
      setSubmitError(t("errorSelectProduct"));
      return;
    }

    if (
      selectedProduct?.hasOsVariants &&
      (!selectedOs ||
        !selectedProduct.osOptions?.some((o) => o.id === selectedOs))
    ) {
      setSubmitError(t("errorSelectOs"));
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch("/api/product/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: emailResult.data,
          productId: selectedProductId,
          locale,
          ...(selectedProduct?.hasOsVariants && selectedOs && { os: selectedOs }),
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setSubmitError(
          data.error ??
            "We couldn't send the email. Please enter a valid email address and try again."
        );
        return;
      }
      setProductName(data.productName ?? selectedProductId);
      setExpiryHours(data.expiryHours ?? 2);
      setEmailSent(true);
      track("cta_click", {
        meta: {
          label: "product_download_request",
          ...(checkoutPlan ? { plan: checkoutPlan } : {}),
          ...(checkoutInterval ? { interval: checkoutInterval } : {}),
        },
      });
    } catch {
      setSubmitError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToForm = () => {
    setEmailSent(false);
    setProductName(null);
  };

  const activateHref = subscribeHref({
    email,
    plan: checkoutPlan,
    interval: checkoutInterval,
  });
  const inlineSubscribeHref = subscribeHref({
    email: email.trim() ? email : undefined,
    plan: checkoutPlan,
    interval: checkoutInterval,
  });

  return (
    <>
      <Header />
      <div className="min-h-screen quant-grid pt-[calc(73px+var(--announcement-bar-height,0px))]">
        <div className="max-w-xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-100 transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              {tc("backToHomeShort")}
            </Link>

            <h1 className="text-3xl md:text-4xl font-bold text-slate-100 mb-2">
              {t("title")}
            </h1>
            <p className="text-slate-400 mb-4">{t("subtitle")}</p>
            <p className="text-slate-300 text-sm mb-8">
              {t("startGuideLead")}{" "}
              <Link
                href="/start"
                className="text-teal-light hover:text-teal-400 font-medium underline-offset-2 hover:underline"
              >
                {t("startGuideLink")}
              </Link>
            </p>

            <div className="mb-10 rounded-xl border border-slate-700/80 bg-slate-800/30 p-5">
              <h2 className="text-sm font-semibold text-slate-100 mb-3">
                {t("expectTitle")}
              </h2>
              <ol className="list-decimal space-y-2 pl-5 text-sm text-slate-400">
                <li>{t("expect1")}</li>
                <li>{t("expect2")}</li>
                <li>{t("expect3", { price: basicMonthlyLabel })}</li>
              </ol>
              <p className="mt-4 text-xs uppercase tracking-wide text-slate-500">
                {t("promptLabel")}
              </p>
              <p className="mt-1 text-sm text-slate-300">{t("samplePrompt")}</p>
            </div>

            {productsLoading ? (
              <div className="h-32 rounded-xl bg-slate-800/30 animate-pulse" />
            ) : emailSent ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 rounded-xl border border-teal-primary/30 bg-slate-800/30 space-y-4"
              >
                <div className="flex items-center gap-3 text-teal-light">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-primary/20">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-100">
                      {t("successTitle")}
                    </p>
                    <p className="text-sm text-slate-400">
                      {t("successBody", {
                        name: productName ?? "",
                        hours: expiryHours,
                      })}
                    </p>
                  </div>
                </div>
                <div className="space-y-3 border-t border-slate-700/70 pt-4">
                  <p className="font-semibold text-slate-100">
                    {t("activateTitle")}
                  </p>
                  <p className="text-sm text-slate-400">{t("activateBody")}</p>
                  <Link
                    href={activateHref}
                    onClick={() =>
                      track("cta_click", { meta: { label: "product_subscribe" } })
                    }
                    className="inline-flex items-center justify-center rounded-lg bg-maroon hover:bg-maroon-hover px-5 py-2.5 text-sm font-semibold text-white transition-colors"
                  >
                    {t("activateButton")}
                  </Link>
                </div>
                <button
                  type="button"
                  onClick={handleBackToForm}
                  className="text-sm text-teal-light hover:text-teal-400 transition-colors font-medium"
                >
                  {t("requestAnother")}
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-3">
                    {t("productLabel")}
                  </label>
                  {products.length === 0 ? (
                    <p className="text-slate-500 text-sm py-4">
                      {t("empty")}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {products.map((product) => {
                        const isSelected =
                          selectedProductId === product.id;
                        return (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => {
                              setSelectedProductId(product.id);
                              setSelectedOs(null);
                            }}
                            className={`flex items-center gap-2 p-4 rounded-xl border-2 text-left transition-all ${
                              isSelected
                                ? "border-teal-primary bg-teal-primary/10"
                                : "border-slate-700 hover:border-slate-600 bg-slate-800/30"
                            }`}
                          >
                            <Package className="w-5 h-5 text-teal-light shrink-0" />
                            <div className="min-w-0">
                              <span className="font-medium text-slate-100 block truncate">
                                {product.name}
                              </span>
                              {product.version && (
                                <span className="text-xs text-slate-500">
                                  v{product.version}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedProduct?.hasOsVariants &&
                  selectedProduct.osOptions &&
                  selectedProduct.osOptions.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-3">
                        {t("osLabel")}
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {selectedProduct.osOptions.map((osOption) => {
                          const isSelected = selectedOs === osOption.id;
                          return (
                            <button
                              key={osOption.id}
                              type="button"
                              onClick={() => setSelectedOs(osOption.id)}
                              className={`flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
                                isSelected
                                  ? "border-teal-primary bg-teal-primary/10"
                                  : "border-slate-700 hover:border-slate-600 bg-slate-800/30"
                              }`}
                            >
                              <Monitor className="w-5 h-5 text-teal-light shrink-0" />
                              <span className="font-medium text-slate-100 text-sm sm:text-base">
                                {osOption.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                {selectedOs && osAssets.length > 0 && (
                  <div>
                    <ul className="space-y-2">
                      {osAssets.map((asset) => (
                        <li
                          key={asset.id}
                          className="flex items-start gap-3 p-3 rounded-lg border border-slate-700/80 bg-slate-800/20"
                        >
                          <Download className="w-4 h-4 text-teal-light shrink-0 mt-0.5" />
                          <div className="min-w-0 flex-1">
                            <p className="text-slate-200 text-sm font-medium">
                              {asset.displayName}
                            </p>
                            <p className="text-slate-500 text-xs mt-0.5">
                              {asset.arch} · {asset.format}
                            </p>
                          </div>
                          {asset.recommended && (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-300/90 bg-amber-500/10 px-2 py-0.5 rounded-full shrink-0">
                              <Star className="w-3 h-3 fill-current" />
                              {t("recommended")}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-slate-300 mb-2"
                  >
                    {t("emailLabel")}
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={handleEmailBlur}
                    placeholder={t("emailPlaceholder")}
                    className={`w-full px-4 py-3 rounded-lg bg-slate-800/50 border text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-primary ${
                      emailError
                        ? "border-red-500/50"
                        : "border-slate-700"
                    }`}
                    autoComplete="email"
                  />
                  {emailError && (
                    <p className="mt-2 text-sm text-red-400">
                      {emailError}
                    </p>
                  )}
                </div>

                {submitError && (
                  <p className="text-sm text-red-400">{submitError}</p>
                )}

                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    products.length === 0 ||
                    (selectedProduct?.hasOsVariants && !selectedOs)
                  }
                  className="w-full py-4 px-6 rounded-lg bg-maroon hover:bg-maroon-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    t("submitting")
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      {t("submit")}
                    </>
                  )}
                </button>
                <Link
                  href={inlineSubscribeHref}
                  onClick={() =>
                    track("cta_click", {
                      meta: { label: "product_subscribe_inline" },
                    })
                  }
                  className="block text-center text-sm text-teal-light hover:text-teal-400 underline underline-offset-2"
                >
                  {t("alreadyInstalled")}
                </Link>
              </form>
            )}
          </motion.div>
        </div>
      </div>
      <Footer />
    </>
  );
}
